import { Utils } from '@common';
import { React, Styled } from '@common/3rd';
import {
	DeskComponentRegistry,
	FlexContainer,
	GridContainer,
	ModelContainerWidget
} from '@component';
import {
	DeskSpeadSheetNoDataComponents,
	DeskSpeadSheetNoDataProps,
	DeskSpreadSheetBodyRowComponents,
	DeskSpreadSheetBodyRowProps,
	DeskSpreadSheetBodyRowsProps,
	DeskSpreadSheetCellComponents,
	DeskSpreadSheetCellProps,
	DeskSpreadSheetComponents,
	DeskSpreadSheetFooterComponents,
	DeskSpreadSheetFooterProps,
	DeskSpreadSheetHeaderComponents,
	DeskSpreadSheetProps
} from '@my-types';

abstract class DeskSpreadSheetCell<
	P extends DeskSpreadSheetCellProps,
	S,
	C extends DeskSpreadSheetCellComponents
> extends FlexContainer<P, S, C> {
	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-spread-sheet-cell'
			})`
				display: flex;
				justify-content: ${this.buildAlignment()};
				${() => this.paintBoxPosition()}
				> span[data-widget=desk-label] {
					height: auto;
					word-break: break-all;
				}
				> div[data-widget=desk-btn-group] > a[data-widget=desk-btn],
				> a[data-widget=desk-btn] {
					margin: ${props => props.theme.BTN_VERTICAL_PADDING - props.theme.BORDER_WIDTH}px 0;
					padding-top: 0;
					padding-bottom: 0;
					line-height: ${props => props.theme.BTN_LINE_HEIGHT + props.theme.BORDER_WIDTH * 2}px;
					height: ${props => props.theme.BTN_LINE_HEIGHT + props.theme.BORDER_WIDTH * 2}px;
					min-width: auto;
				}
				> div[data-widget=desk-btn-group] > a[data-widget=desk-btn] {
					&:first-child {
						border-top-right-radius: 0;
						border-bottom-right-radius: 0;
					}
					&:not(:first-child) {
						margin-left: ${props => props.theme.BTN_GROUP_GAP}px;
						border-top-left-radius: 0;
						border-bottom-left-radius: 0;
						&:before {
							content: '';
							display: block;
							position: absolute;
							left: -0.5px;
							top: 20%;
							height: 60%;
							border-left: ${props => props.theme.BORDER};
							border-color: ${props => props.theme.BTN_GROUP_GAP_COLOR};
						}
					}
				}
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}
	protected paintBoxPosition(): string {
		const props = this.props;
		return `
			${props.row ? `grid-row-start: ${props.row};` : ''}
			${props.rowSpan ? `grid-row-end: span ${props.rowSpan};` : ''}
			${props.column ? `grid-column-start: ${props.column};` : ''}
			${props.columnSpan ? `grid-column-end: span ${props.columnSpan};` : ''}
		`;
	}
	render() {
		const C = this.getComponents();
		return <C.Box>{this.renderChildren()}</C.Box>;
	}
}

class DeskSpreadSheetHeader<P, S, C extends DeskSpreadSheetHeaderComponents> extends GridContainer<
	P,
	S,
	C
> {
	static getConcernedChildren() {
		return {
			title: 'spread.header.cell'
		};
	}
	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-spread-sheet-header'
			})`
				display: grid;
				grid-column-gap: ${props => props.theme.PANEL_CELL_HORIZONTAL_GAP}px;
				align-items: start;
				grid-template-columns: ${() => this.buildGridTemplateAreas()};
				border-bottom: ${props => props.theme.BORDER};
				border-color: ${props => props.theme.COLOR_PRIMARY};
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}
	render() {
		const C = this.getComponents();
		return <C.Box>{this.renderChildren()}</C.Box>;
	}
}

class DeskSpreadSheetHeaderCell<
	P extends DeskSpreadSheetCellProps,
	S,
	C extends DeskSpreadSheetCellComponents
> extends DeskSpreadSheetCell<P, S, C> {
	protected initComponents(): C {
		const C = super.initComponents();
		return {
			Box: Styled(C.Box)`
				font-weight: ${props => props.theme.FONT_WEIGHT_BOLD_LIGHT};
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}
}

class DeskSpreadSheetBody<P, S, C extends DeskSpreadSheetHeaderComponents> extends GridContainer<
	P,
	S,
	C
> {
	static getConcernedChildren() {
		return {
			row: 'spread.body.rows'
		};
	}
	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-spread-sheet-body'
			})`
				display: flex;
				flex-direction: column;
				border-bottom: ${props => props.theme.BORDER};
				border-color: ${props => props.theme.COLOR_PRIMARY};
				> div:nth-child(2n) {
					background-color: ${props => props.theme.SPREAD_EVEN_BG_COLOR};
				}
				> div {
					border-radius: ${props => props.theme.BORDER_RADIUS}px;
					&:hover {
						background-color: ${props => props.theme.SPREAD_HOVER_BG_COLOR};
					}
				}
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}
	render() {
		const C = this.getComponents();
		return <C.Box>{this.renderChildren()}</C.Box>;
	}
}

class DeskSpreadSheetNoData<
	P extends DeskSpeadSheetNoDataProps,
	S,
	C extends DeskSpeadSheetNoDataComponents
> extends FlexContainer<P, S, C> {
	protected initComponents() {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-spread-sheet-nodata'
			})`
				display: flex;
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}
	render() {
		const C = this.getComponents();
		return (
			<C.Box>
				{this.getLabel()}
				{this.renderChildren()}
			</C.Box>
		);
	}
	getLabel() {
		if (this.props.label === false) {
			return null;
		} else {
			return this.props.label || '当前没有数据需要显示.';
		}
	}
}

/**
 * 行代理, 任务是循环描画行, 并且将每行的内容传给行组件
 */
class DeskSpreadSheetBodyRows<
	P extends DeskSpreadSheetBodyRowsProps & {sorter?:()=>number},
	S,
	C
> extends ModelContainerWidget<P, S, C> {
	static getConcernedChildren() {
		return {
			nodata: 'spread.body.nodata',
			cells: 'spread.body.cell',
			children: 'spread.body.cell'
		};
	}
	protected initComponents(): C {
		return {} as C;
	}
	protected renderNoData(): any {
		const nodata = React.Children.toArray(this.props.children).find(
			(child: any) => child.type.name === 'DeskSpreadSheetNoData'
		);
		// 没有定义过, 强制画一个默认的
		return nodata || <DeskSpreadSheetNoData />;
	}
	protected renderDataRow(rowIndex: number): any {
		return React.Children.toArray(this.props.children).filter(
			(child: any) => child.type.name !== 'DeskSpreadSheetNoData'
		);
	}
	/**
	 * 数组嵌套
	 */
	protected renderChildren(): any {
		const values = Utils.toArray(this.getValueFromModel());
		if (values.length === 0) {
			if (this.shouldRenderNoData()) {
				return this.renderNoData();
			}
		} else {
			const props = this.props;
			return Utils.toArray(this.getValueFromModel()).map((row, rowIndex) => {
				return (
					<DeskSpreadSheetBodyRow
						{...props}
						key={rowIndex}
						model={row}
						arrayHolder={this.getModel()}>
						{this.renderDataRow(rowIndex)}
					</DeskSpreadSheetBodyRow>
				);
			});
		}
	}
	render() {
		return <React.Fragment>{this.renderChildren()}</React.Fragment>;
	}
	shouldRenderNoData() {
		return this.props.nodata !== false;
	}

	getValueFromModel(){
		const filteredData = Utils.toArray(super.getValueFromModel());
		const sorter = this.getSorter();
		return sorter ? filteredData.sort(sorter) : filteredData;
	}

	getSorter() {
		return this.props.sorter;
	}
}

class DeskSpreadSheetBodyRow<
	P extends DeskSpreadSheetBodyRowProps,
	S,
	C extends DeskSpreadSheetBodyRowComponents
> extends GridContainer<P, S, C> {
	protected initComponents() {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-spread-sheet-body-row'
			})`
				display: grid;
				grid-column-gap: ${props => props.theme.PANEL_CELL_HORIZONTAL_GAP}px;
				align-items: start;
				grid-template-columns: ${() => this.buildGridTemplateAreas()};
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}
	render() {
		const C = this.getComponents();
		return <C.Box>{this.renderChildren()}</C.Box>;
	}
}

class DeskSpreadSheetBodyCell<
	P extends DeskSpreadSheetCellProps,
	S,
	C extends DeskSpreadSheetCellComponents
> extends DeskSpreadSheetCell<P, S, C> {
	static getConcernedChildren() {
		return {
			input: 'label'
		};
	}
	render() {
		return super.render();
	}
}

class DeskSpreadSheetFooter<
	P extends DeskSpreadSheetFooterProps,
	S,
	C extends DeskSpreadSheetFooterComponents
> extends FlexContainer<P, S, C> {
	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-spread-sheet-footer'
			})`
			display: flex;
			justify-content: ${this.buildAlignment()};
			${props => this.renderCSS(props.theme)}
		`
		} as C;
	}
	render() {
		const C = this.getComponents();
		return <C.Box>{this.renderChildren()}</C.Box>;
	}
}

/**
 * 桌面表格
 */
class DeskSpreadSheet<
	P extends DeskSpreadSheetProps,
	S,
	C extends DeskSpreadSheetComponents
> extends ModelContainerWidget<P, S, C> {
	static getConcernedChildren() {
		return {
			header: 'spread.header',
			body: 'spread.body',
			footer: 'spread.footer'
		};
	}
	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-spread-sheet'
			})`
				display: flex;
				flex-direction: column;
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}
	render() {
		const C = this.getComponents();
		return <C.Box>{this.renderChildren()}</C.Box>;
	}
}
DeskComponentRegistry.register({
	spread: DeskSpreadSheet,
	'spread.header': DeskSpreadSheetHeader,
	'spread.header.cell': DeskSpreadSheetHeaderCell,
	'spread.body': DeskSpreadSheetBody,
	'spread.body.rows': DeskSpreadSheetBodyRows,
	'spread.body.row': DeskSpreadSheetBodyRow,
	'spread.body.cell': DeskSpreadSheetBodyCell,
	'spread.body.nodata': DeskSpreadSheetNoData,
	'spread.footer': DeskSpreadSheetFooter
});

export {
	DeskSpreadSheet,
	DeskSpreadSheetHeader,
	DeskSpreadSheetHeaderCell,
	DeskSpreadSheetBody,
	DeskSpreadSheetBodyRow,
	DeskSpreadSheetBodyCell,
	DeskSpreadSheetNoData,
	DeskSpreadSheetFooter
};
